import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useNavigate } from 'react-router-dom';
import { makeStyles } from '@mui/styles';
import { Button, FormControl, InputLabel, MenuItem, Select, styled, useTheme } from '@mui/material';
import HomeTile from './HomeTile';
import PLIBannerTitle from './PLIBannerTile';
import { translate } from '../translations/translator';
import { getNewLink } from '../util/workbook-util';
import LanguageDropdown from 'components/@common/Dropdowns/LanguageDropdown';
import lessonPlannerIcon from 'assets/images/icons/LessonPlannerCircled.png';
import assignmentIcon from 'assets/images/icons/AssignmentTrackerCircled.png';
import resourcesIcon from 'assets/images/icons/ResourcesCircled.png';
import formationsIcon from 'assets/images/icons/DigitalFormationTools.png';
import worksheetMakerIcon from 'assets/images/icons/APlusWorkSheetMakerCircled.png';
import { remCalc } from '../util/style-util';
import { logoutUser } from 'state/modules/user/actions';
import { AppStoreState } from 'types';
import { selectWorkbook } from 'state/modules/app';
import { FONT_GSS_FUTURA } from 'themes/default';
import WebLink from 'components/@common/WebLink';
import clickAway from 'assets/images/hitt-logo-round.png';
import { getLicenseIsTrial, hasClickAwayOnly } from 'state/modules/user/selectors/user-selectors';
import StyledBaseInput from 'components/@common/Inputs/StyledBaseInput';

const translateHome = key => translate('home', key);

const ColorButton = styled(Button)(() => ({
  marginLeft: remCalc(10),
  marginTop: remCalc(-8),
  padding: remCalc(10),
  width: remCalc(160),
  color: '#ffffff',
  backgroundColor: '#000000',
  fontFamily: FONT_GSS_FUTURA,
  fontSize: '16px',
  textTransform: 'none',
  borderRadius: '8px',
  '&:hover': {
    backgroundColor: '#000000',
    transform: 'scale(1.05, 1.05)'
  }
}));

const Home: React.FC = () => {
  const navigate = useNavigate();

  const user = useSelector(({ user }: AppStoreState) => user);
  const isClickAway = useSelector(state => hasClickAwayOnly(state));
  const allBooks = useSelector(({ entity }: AppStoreState) => entity.workbooks);
  const workbooks = allBooks.filter(book => book.type === 'workbook');
  const teacherGuides = allBooks.filter(book => book.type === 'teachersGuide');
  const currentWorkbook: any = useSelector(({ app }: AppStoreState) => app.currentWorkbook);
  const dispatch = useDispatch() as any;

  const isValidTrialUser: boolean = useSelector(getLicenseIsTrial);

  const theme = useTheme();

  const {
    containerPage,
    headerContainer,
    mainContent,
    styledRule,
    workbookDropdown,
    workbookDropdownItem,
    help,
    helpLink,
    clickAwayText,
    clickAwayBadge
  } = useStyles(theme)({ currentWorkbook }) as any;

  const teacherGuide = teacherGuides.find(
    tg =>
      tg.products?.[0]?.productCurriculumId === currentWorkbook?.products?.[0]?.productCurriculumId
  );

  const userId = user.me ? user.me.id : 0;

  const handleChange = async e => {
    const nextWorkbook = workbooks.find(x => x.id === e.target.value);
    await dispatch(selectWorkbook(nextWorkbook));
    navigate(`/home/${nextWorkbook?.bookCode}/${nextWorkbook?.edition}`);
  };

  return (
    <section className={containerPage}>
      <div className={mainContent}>
        <span className={headerContainer}>
          <div>
            <span style={{ fontSize: '40px', fontWeight: 200 }}>{translateHome('greeting')}</span>
            <span
              style={{ fontSize: '40px', fontWeight: 700 }}
            >{`${user.me?.firstName} ${user.me?.lastName}`}</span>
            <br />
            <span style={{ fontSize: '24px' }}>{translateHome('instructions')}</span>
          </div>
          <div style={{ display: 'flex' }}>
            <img
              src={currentWorkbook?.combinedCoverImageUrl}
              height={remCalc(1200)}
              alt={currentWorkbook?.title}
            />
            <div style={{ margin: '10px' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  paddingBottom: '20px'
                }}
              >
                {currentWorkbook && (
                  <FormControl
                    className={workbookDropdown}
                    sx={{ marginBottom: '26px', height: '50px' }}
                    variant="standard"
                  >
                    <InputLabel
                      id="workbookLabel"
                      style={{
                        fontSize: '15px',
                        color: 'black',
                        marginLeft: remCalc(3)
                      }}
                    >
                      {translateHome('selectWorkbook')}
                    </InputLabel>
                    <Select
                      labelId="workbookLabel"
                      className={workbookDropdown}
                      value={currentWorkbook?.id}
                      onChange={handleChange}
                      input={<StyledBaseInput />}
                    >
                      {workbooks.map(workbook => (
                        <MenuItem
                          key={workbook.bookCode}
                          value={workbook?.id}
                          className={workbookDropdownItem}
                        >
                          {workbook.title} (Grade {workbook.gradeLevel})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <ColorButton onClick={() => dispatch(logoutUser())} variant={'contained'}>
                  {translateHome('logout')}
                </ColorButton>
              </div>
            </div>
          </div>
        </span>
        <hr className={styledRule} />
        {isClickAway && (
          <div className={clickAwayText}>
            <img src={clickAway} alt="ClickAwayBadge" className={clickAwayBadge} />
            {`${translateHome('clickAwayBanner')} `}
            <WebLink
              style={{ textDecoration: 'underline', color: theme.palette.secondary.main }}
              to="https://www.lwtears.com/IDTT"
              target="_blank"
              name={translateHome('clickAwayBuyLink')}
            />
          </div>
        )}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            paddingBottom: '5px'
          }}
        >
          <HomeTile
            title={translateHome('studentWorkbook')}
            icon={currentWorkbook?.coverImageUrl}
            isDisabled={isClickAway}
            onClick={() =>
              navigate(
                getNewLink(currentWorkbook?.bookCode, currentWorkbook?.edition, 'workbook', userId)
              )
            }
          />
          <HomeTile
            title={translateHome('teacherGuide')}
            icon={teacherGuide?.coverImageUrl}
            isDisabled={isClickAway}
            onClick={() =>
              navigate(
                getNewLink(
                  currentWorkbook?.bookCode,
                  currentWorkbook?.edition,
                  'teachersGuide',
                  userId
                )
              )
            }
          />
          <HomeTile
            title={translateHome('lessonPlanner')}
            icon={lessonPlannerIcon}
            isDisabled={isClickAway}
            onClick={() =>
              navigate(
                getNewLink(currentWorkbook?.bookCode, currentWorkbook?.edition, 'lessonPlanner')
              )
            }
          />
          <HomeTile
            title={translateHome('assignment')}
            icon={assignmentIcon}
            isDisabled={isClickAway || user.isAuditor || isValidTrialUser}
            onClick={() =>
              navigate(
                getNewLink(currentWorkbook?.bookCode, currentWorkbook?.edition, 'assignmentTracker')
              )
            }
          />
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            paddingBottom: '20px'
          }}
        >
          <HomeTile
            title={translateHome('resources')}
            icon={resourcesIcon}
            isDisabled={false}
            onClick={() =>
              navigate(
                isClickAway
                  ? getNewLink(currentWorkbook?.bookCode, currentWorkbook?.edition, 'clickAways')
                  : getNewLink(currentWorkbook?.bookCode, currentWorkbook?.edition, 'resources')
              )
            }
          />
          <HomeTile
            title={translateHome('apps')}
            icon={formationsIcon}
            isDisabled={isClickAway}
            onClick={() =>
              navigate(getNewLink(currentWorkbook?.bookCode, currentWorkbook?.edition, 'lnf'))
            }
          />
          <HomeTile
            title={translateHome('worksheetMaker')}
            icon={worksheetMakerIcon}
            isDisabled={isClickAway}
            onClick={() =>
              navigate(getNewLink(currentWorkbook?.bookCode, currentWorkbook?.edition, 'wm'))
            }
          />
        </div>
        <div style={{ display: 'flex' }}>
          <LanguageDropdown />
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            paddingBottom: '20px'
          }}
        >
          <PLIBannerTitle
            onClick={() =>
              window.open(
                getNewLink(currentWorkbook?.bookCode, currentWorkbook?.edition, 'pli'),
                '_blank'
              )
            }
          />
        </div>
        <div style={{ textAlign: 'center' }}>
          <span className={help}>{translateHome('needHelp')}</span>{' '}
          <NavLink
            className={helpLink}
            to={getNewLink(currentWorkbook?.bookCode, currentWorkbook?.edition, 'support')}
          >
            {translateHome('supportOptions')}
          </NavLink>
        </div>
      </div>
    </section>
  );
};

export default Home;

const useStyles = theme =>
  makeStyles(() => ({
    containerPage: () => ({
      height: '-webkit-fill-available',
      display: 'flex',
      flexDirection: 'column'
    }),
    headerContainer: {
      paddingTop: remCalc(30),
      display: 'flex',
      width: '100%',
      height: '140px',
      fontWeight: 400,
      justifyContent: 'space-between'
    },
    mainContent: {
      flex: 1,
      width: '90%',
      margin: 'auto',
      maxWidth: '1400px',
      minWidth: '1200px'
    },
    styledRule: () => ({
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.main,
      height: remCalc(1),
      width: '100%',
      marginTop: '0px'
    }),
    workbookDropdown: {
      height: '70px',
      borderRadius: '8px',
      fontWeight: '300 !important',
      width: '320px',
      '&.MuiSelect-select': {
        backgroundColor: '#FFFFFF',
        '&:focus': {
          backgroundColor: '#FFFFFF'
        }
      }
    },
    workbookDropdownItem: {
      marginTop: remCalc(2),
      padding: '10px',
      fontWeight: '300 !important',
      fontSize: '16px',
      width: '320px'
    },
    help: {
      fontWeight: 300,
      fontSize: '20px'
    },
    helpLink: {
      fontWeight: 300,
      fontSize: '20px',
      textDecoration: 'underline'
    },
    clickAwayText: {
      fontWeight: 300,
      fontSize: '18px',
      textAlign: 'center',
      fontFamily: FONT_GSS_FUTURA,
      color: '#444444',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    },
    clickAwayBadge: {
      width: remCalc(35),
      marginRight: remCalc(10)
    }
  }));
